import { useEffect, useRef, useSyncExternalStore } from "react";

/* Refresh policy for fleet-sized lists (/api/units and the tile view).
 *
 * The dashboard WebSocket (/ws/events) publishes session.started / session.ended
 * only — it says nothing about a unit going online/offline or changing state —
 * so polling stays, but as a slow fallback:
 *   - WS open:  every FLEET_POLL_WS_OPEN_MS, plus an invalidation per session
 *               event (coalesced in useWsEvents);
 *   - WS down:  every FLEET_POLL_WS_DOWN_MS;
 *   - hidden tab: never (React Query pauses refetchInterval in the background;
 *               useVisibleInterval does the same for hand-rolled timers);
 *   - WS reconnect: one refetch, since events may have been missed. */

export const FLEET_POLL_WS_OPEN_MS = 60_000;
export const FLEET_POLL_WS_DOWN_MS = 30_000;

export type WsStatus = "idle" | "connecting" | "open" | "error";

let wsStatus: WsStatus = "idle";
const wsListeners = new Set<() => void>();

export function setWsStatus(next: WsStatus): void {
  if (next === wsStatus) return;
  wsStatus = next;
  wsListeners.forEach((l) => l());
}

export function getWsStatus(): WsStatus {
  return wsStatus;
}

function subscribeWs(listener: () => void): () => void {
  wsListeners.add(listener);
  return () => wsListeners.delete(listener);
}

export function useWsStatus(): WsStatus {
  return useSyncExternalStore(subscribeWs, getWsStatus, getWsStatus);
}

export function fleetPollInterval(status: WsStatus): number {
  return status === "open" ? FLEET_POLL_WS_OPEN_MS : FLEET_POLL_WS_DOWN_MS;
}

export function useFleetPollInterval(): number {
  return fleetPollInterval(useWsStatus());
}

/* Why the next fleet-list request is made — sent as X-Refresh-Source so the
 * backend can count polling vs event-driven refreshes. A marker is valid for a
 * short window because one invalidation refetches every mounted list query. */
export type RefreshSource = "initial" | "poll" | "ws" | "reconnect" | "user" | "focus";

const SOURCE_WINDOW_MS = 2_000;
let marked: { source: RefreshSource; at: number } | null = null;

export function markRefreshSource(source: RefreshSource): void {
  marked = { source, at: Date.now() };
}

export function refreshSource(hasData: boolean): RefreshSource {
  if (marked && Date.now() - marked.at < SOURCE_WINDOW_MS) return marked.source;
  if (!hasData) return "initial";
  if (typeof document !== "undefined" && document.visibilityState === "visible" && justFocused()) {
    return "focus";
  }
  return "poll";
}

let lastVisibleAt = 0;
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") lastVisibleAt = Date.now();
  });
}

function justFocused(): boolean {
  return Date.now() - lastVisibleAt < SOURCE_WINDOW_MS;
}

/* Units-list refresh signal for views that do not use React Query (the admin
 * tile view): bumped whenever the WS layer invalidates ["units"]. */
let unitsRefreshTick = 0;
const tickListeners = new Set<() => void>();

export function bumpUnitsRefresh(): void {
  unitsRefreshTick += 1;
  tickListeners.forEach((l) => l());
}

function subscribeTick(listener: () => void): () => void {
  tickListeners.add(listener);
  return () => tickListeners.delete(listener);
}

export function useUnitsRefreshTick(): number {
  return useSyncExternalStore(subscribeTick, () => unitsRefreshTick, () => unitsRefreshTick);
}

/* setInterval that does not run while the tab is hidden, and fires once on
 * becoming visible again if a tick was due meanwhile. `ms` null disables it. */
export function useVisibleInterval(fn: () => void, ms: number | null): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (ms == null) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    let lastRun = Date.now();

    const run = () => {
      lastRun = Date.now();
      fnRef.current();
    };
    const start = () => {
      if (timer === null) timer = setInterval(run, ms);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        stop();
      } else {
        if (Date.now() - lastRun >= ms) run();
        start();
      }
    };

    if (document.visibilityState !== "hidden") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [ms]);
}
